import { Box, Divider, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material"
import { getCountryByCode } from "postal-code-checker"
import React from "react"
import { Controller, useFormContext } from "react-hook-form"

import AS400PreviewForm from "./AS400PreviewForm"
import { LocationMenuDialogs } from "./types/dialogs"
import { updateAS400Field } from "./utils/utils"

import { AutoComplete } from "@/components/common/Autocomplete"
import {
  FIELD_ERROR_CLASSES,
  FIELD_LABEL_CLASSES,
  SELECT_STYLING_PROPS,
  TEXT_FIELD_STYLING_PROPS,
} from "@/constants/forms"
import { PermissionActionType } from "@/constants/permissions.ts"
import type { Country, Region } from "@/graphql/codegen/graphql"
import { ExternalLocationType } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager.ts"
import type { AddAddressQueryProps } from "@/screens/Companies/types/dialogProps"
import type { Option } from "@/types/form-fields"
import { validatePostalCode } from "@/utils/postalCode"

interface Props {
  countryList: Country[]
  regionList: Region[]
  modalType: LocationMenuDialogs
  locationType?: ExternalLocationType
  managers?: {
    name: string
    id: string
  }[]

  isCustomerCompany?: boolean
  salesPersonOptions?: Option<string>[]
}

export const LocationAddressDetailsForm = ({
  countryList = [],
  regionList = [],
  managers = [],
  salesPersonOptions = [],
  modalType,
  isCustomerCompany = false,
  locationType,
}: Props): React.ReactElement => {
  const methods = useFormContext<AddAddressQueryProps>()
  const countryIsoCode = methods.watch("countryIsoCode")
  const { hasPermissions } = usePermissionManager()

  const updateParams = {
    geoLocations: { regionList, countryList },
    formValues: methods.watch(),
    methods,
  }

  const shouldDisableBasedOnLocationType = [ExternalLocationType.Remit, ExternalLocationType.Bill].includes(
    locationType as ExternalLocationType
  )

  const isAddressModal = modalType === LocationMenuDialogs.ADD_ADDRESS

  const isAvailableToDisplaySalesman =
    isAddressModal && isCustomerCompany && hasPermissions([PermissionActionType.CUSTOMER_MAINTENANCE])

  const isAvailableToDisplayAccountManager =
    isAddressModal && !isCustomerCompany && hasPermissions([PermissionActionType.VENDOR_MAINTENANCE])

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const postalCode = e.target.value

    if (postalCode && countryIsoCode) {
      const selectedCountry = countryList.find((c: Country) => c.isoCode === countryIsoCode)
      const isValid = validatePostalCode(selectedCountry?.countryCode ?? "US", postalCode)

      if (isValid) {
        methods.clearErrors("postalCode")
        methods.trigger("postalCode")
      } else {
        const country = getCountryByCode(selectedCountry?.countryCode ?? "US")

        methods.setError("postalCode", {
          message: `Postal code is invalid. Example: ${country?.examplePostalCodes}`,
          type: "manual",
        })
      }
    } else {
      methods.clearErrors("postalCode")
      methods.trigger("postalCode")
    }

    methods.setValue("postalCode", postalCode)

    updateAS400Field({
      key: "postalCode",
      fieldValue: e.target.value,
      updateParams,
    })
  }

  return (
    <Box className='flex w-full gap-8'>
      <form className='flex-1'>
        <Typography
          data-testid='location-address-details-form-title'
          variant='body1'
          fontWeight={700}
          className='h-9 leading-6 tracking-[0.15px] text-gray-700'
        >
          Location Details
        </Typography>

        <Box className='space-y-6'>
          <Controller
            name='name'
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <Box className='mt-6' data-testid='location-address-details-form-address1'>
                <label htmlFor='address1' className={FIELD_LABEL_CLASSES}>
                  Location Name<span className='text-red-600'>*</span>
                </label>
                <TextField
                  {...field}
                  {...TEXT_FIELD_STYLING_PROPS}
                  id='name'
                  autoComplete='off'
                  placeholder='Descriptive location nickname'
                  error={!!error}
                  disabled={shouldDisableBasedOnLocationType}
                  onChange={(e) => {
                    field.onChange(e)

                    updateAS400Field({
                      key: "name",
                      fieldValue: e.target.value,
                      updateParams,
                    })
                  }}
                />
                {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
              </Box>
            )}
          />
          <Controller
            name='description'
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <Box className='mt-6' data-testid='location-address-details-form-address1'>
                <label htmlFor='address1' className={FIELD_LABEL_CLASSES}>
                  Location Description<span className='text-red-600'>*</span>
                </label>
                <TextField
                  {...field}
                  {...TEXT_FIELD_STYLING_PROPS}
                  id='description'
                  placeholder='Location Description'
                  error={!!error}
                  autoComplete='off'
                  disabled={shouldDisableBasedOnLocationType}
                />
                {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
              </Box>
            )}
          />
          <Controller
            name='address1'
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <Box className='mt-6' data-testid='location-address-details-form-address1'>
                <label htmlFor='address1' className={FIELD_LABEL_CLASSES}>
                  Address Line 1<span className='text-red-600'>*</span>
                </label>
                <TextField
                  {...field}
                  {...TEXT_FIELD_STYLING_PROPS}
                  id='address1'
                  placeholder='Avenue or Street #'
                  error={!!error}
                  autoComplete='off'
                  onChange={(e) => {
                    field.onChange(e)

                    updateAS400Field({
                      key: "address1",
                      fieldValue: e.target.value,
                      updateParams,
                    })
                  }}
                />
                {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
              </Box>
            )}
          />
          <Controller
            name='address2'
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <Box className='mt-6' data-testid='location-address-details-form-address2'>
                <label htmlFor='address2' className={FIELD_LABEL_CLASSES}>
                  Address Line 2
                </label>
                <TextField
                  {...field}
                  {...TEXT_FIELD_STYLING_PROPS}
                  id='address2'
                  placeholder='Suite, apartment #'
                  error={!!error}
                  onChange={(e) => {
                    field.onChange(e)

                    updateAS400Field({
                      key: "address2",
                      fieldValue: e.target.value,
                      updateParams,
                    })
                  }}
                />
                {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
              </Box>
            )}
          />
          <Controller
            name='countryIsoCode'
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <FormControl className='mt-6' fullWidth data-testid='location-address-details-form-country'>
                <InputLabel id='countryIsoCode' className={FIELD_LABEL_CLASSES}>
                  Country<span className='text-red-600'>*</span>
                </InputLabel>
                <Select
                  {...SELECT_STYLING_PROPS}
                  {...field}
                  labelId='countryIsoCode'
                  value={field.value ?? ""}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span className='text-gray-500'>Select One</span>
                    }

                    const selectedCountry = countryList.find((c: Country) => c.isoCode === selected)
                    return selectedCountry ? selectedCountry.name : ""
                  }}
                  error={!!error}
                  onChange={(e) => {
                    field.onChange(e)
                    const value = e.target.value

                    updateAS400Field({
                      key: "address2",
                      fieldValue: updateParams.formValues.address2,
                      updateParams: {
                        ...updateParams,
                        formValues: {
                          ...updateParams.formValues,
                          countryIsoCode: value,
                        },
                      },
                    })

                    updateAS400Field({
                      key: "city",
                      fieldValue: updateParams.formValues.city,
                      updateParams: {
                        ...updateParams,
                        formValues: {
                          ...updateParams.formValues,
                          countryIsoCode: value,
                          regionIsoCode: "",
                        },
                      },
                    })

                    updateAS400Field({
                      key: "regionIsoCode",
                      fieldValue: null,
                      updateParams: {
                        ...updateParams,
                        formValues: {
                          ...updateParams.formValues,
                          countryIsoCode: value,
                          regionIsoCode: "",
                        },
                      },
                    })
                  }}
                >
                  {countryList.map(({ isoCode, name }) => (
                    <MenuItem key={isoCode} value={isoCode}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
                {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
              </FormControl>
            )}
          />
          <Controller
            name='regionIsoCode'
            control={methods.control}
            render={({ field }) => (
              <FormControl className='mt-6' fullWidth data-testid='location-address-details-form-region'>
                <InputLabel id='regionIsoCode' className={FIELD_LABEL_CLASSES}>
                  State (Province / Department / Region)
                </InputLabel>
                <Select
                  {...field}
                  {...SELECT_STYLING_PROPS}
                  labelId='regionIsoCode'
                  disabled={!countryIsoCode || countryIsoCode?.length === 0}
                  value={field.value && regionList.some((r) => r.isoCode === field.value) ? field.value : ""}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span className='text-gray-500'>Select One</span>
                    }

                    return regionList.find((r) => r.isoCode === selected)?.name ?? ""
                  }}
                  onChange={(e) => {
                    field.onChange(e)
                    const value = e.target.value

                    updateAS400Field({
                      key: "regionIsoCode",
                      fieldValue: value,
                      updateParams: {
                        ...updateParams,
                        formValues: {
                          ...updateParams.formValues,
                          regionIsoCode: value,
                        },
                      },
                    })

                    updateAS400Field({
                      key: "city",
                      fieldValue: updateParams.formValues.city,
                      updateParams: {
                        ...updateParams,
                        formValues: {
                          ...updateParams.formValues,
                          regionIsoCode: value,
                        },
                      },
                    })
                  }}
                >
                  {regionList.map((region) => (
                    <MenuItem key={region.isoCode} value={region.isoCode ?? ""}>
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
          <Controller
            name='city'
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <Box className='mt-6' data-testid='location-address-details-form-city'>
                <label htmlFor='city' className={FIELD_LABEL_CLASSES}>
                  City<span className='text-red-600'>*</span>
                </label>
                <TextField
                  {...field}
                  {...TEXT_FIELD_STYLING_PROPS}
                  id='city'
                  placeholder='City, Town, Village'
                  error={!!error}
                  autoComplete='off'
                  onChange={(e) => {
                    field.onChange(e)

                    updateAS400Field({
                      key: "city",
                      fieldValue: e.target.value,
                      updateParams,
                    })

                    updateAS400Field({
                      key: "address2",
                      fieldValue: updateParams.formValues.address2,
                      updateParams: {
                        ...updateParams,
                        formValues: {
                          ...updateParams.formValues,
                          city: e.target.value,
                        },
                      },
                    })
                  }}
                />
                {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
              </Box>
            )}
          />
          <Controller
            name='postalCode'
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <Box className='mt-6' data-testid='location-address-details-form-postal-code'>
                <label htmlFor='postalCode' className={FIELD_LABEL_CLASSES}>
                  ZIP Code (Postal Code)
                </label>
                <TextField
                  {...field}
                  {...TEXT_FIELD_STYLING_PROPS}
                  id='postalCode'
                  value={field.value || ""}
                  placeholder='00000'
                  error={!!error}
                  onChange={handlePostalCodeChange}
                />
                {error?.message && <p className={FIELD_ERROR_CLASSES}>{error.message}</p>}
              </Box>
            )}
          />

          {isAvailableToDisplayAccountManager && (
            <Controller
              name='accountManager'
              control={methods.control}
              render={() => (
                <Box data-testid='location-address-details-form-postal-code'>
                  <AutoComplete
                    label='Account Manager'
                    placeholder='Select Account Manager'
                    name='accountManager'
                    id='accountManager'
                    options={managers}
                    getOptionLabel={({ name }) => name}
                    control={methods.control}
                    clearOnEscape
                  />
                </Box>
              )}
            />
          )}

          {isAvailableToDisplaySalesman && (
            <Controller
              name='salesman'
              control={methods.control}
              render={() => (
                <Box data-testid='location-address-details-form-salesman'>
                  <AutoComplete<Option<string>>
                    label='Salesperson'
                    placeholder='Select Salesperson'
                    name='salesman'
                    id='salesman'
                    options={salesPersonOptions}
                    getOptionLabel={({ label }) => label}
                    control={methods.control}
                    clearOnEscape
                  />
                </Box>
              )}
            />
          )}
        </Box>
      </form>
      <Divider orientation='vertical' flexItem />
      <form className='flex-1'>
        <AS400PreviewForm />
      </form>
    </Box>
  )
}
